(function () {
  if (!window.customElements) return;
  if (customElements.get('price-range')) return;

  class PriceRangeElement extends HTMLElement {
    connectedCallback() {
      if (this._initialized) {
        this.syncFromCurrentValues();
        return;
      }

      this._initialized = true;
      this._form = this.closest('form');
      this._lowerBound = this.parseNumber(this.dataset.rangeMin, 0);
      this._upperBound = this.parseNumber(
        this.dataset.rangeMax,
        this._lowerBound
      );
      this._step = this.parseNumber(this.dataset.step, 1);

      this._minRange = this.querySelector('[data-price-range-slider="min"]');
      this._maxRange = this.querySelector('[data-price-range-slider="max"]');
      this._minInput = this.querySelector('[data-price-range-input="min"]');
      this._maxInput = this.querySelector('[data-price-range-input="max"]');
      this._minHiddenInput = this.querySelector(
        '[data-price-range-hidden="min"]'
      );
      this._maxHiddenInput = this.querySelector(
        '[data-price-range-hidden="max"]'
      );

      if (
        !(this._minRange instanceof HTMLInputElement) ||
        !(this._maxRange instanceof HTMLInputElement) ||
        !(this._minInput instanceof HTMLInputElement) ||
        !(this._maxInput instanceof HTMLInputElement) ||
        !(this._minHiddenInput instanceof HTMLInputElement) ||
        !(this._maxHiddenInput instanceof HTMLInputElement)
      ) {
        return;
      }

      this._onRangeInput = this.onRangeInput.bind(this);
      this._onNumberInput = this.onNumberInput.bind(this);
      this._onCommit = this.onCommit.bind(this);
      this._onNumberKeydown = this.onNumberKeydown.bind(this);
      this._onFormSubmit = this.onFormSubmit.bind(this);

      this._minRange.addEventListener('input', this._onRangeInput);
      this._maxRange.addEventListener('input', this._onRangeInput);
      this._minRange.addEventListener('change', this._onCommit);
      this._maxRange.addEventListener('change', this._onCommit);

      this._minInput.addEventListener('input', this._onNumberInput);
      this._maxInput.addEventListener('input', this._onNumberInput);
      this._minInput.addEventListener('change', this._onCommit);
      this._maxInput.addEventListener('change', this._onCommit);
      this._minInput.addEventListener('keydown', this._onNumberKeydown);
      this._maxInput.addEventListener('keydown', this._onNumberKeydown);

      if (this._form) {
        this._form.addEventListener('submit', this._onFormSubmit);
      }

      this.syncFromCurrentValues();
    }

    disconnectedCallback() {
      if (this._minRange && this._onRangeInput) {
        this._minRange.removeEventListener('input', this._onRangeInput);
        this._minRange.removeEventListener('change', this._onCommit);
      }

      if (this._maxRange && this._onRangeInput) {
        this._maxRange.removeEventListener('input', this._onRangeInput);
        this._maxRange.removeEventListener('change', this._onCommit);
      }

      if (this._minInput && this._onNumberInput) {
        this._minInput.removeEventListener('input', this._onNumberInput);
        this._minInput.removeEventListener('change', this._onCommit);
        this._minInput.removeEventListener('keydown', this._onNumberKeydown);
      }

      if (this._maxInput && this._onNumberInput) {
        this._maxInput.removeEventListener('input', this._onNumberInput);
        this._maxInput.removeEventListener('change', this._onCommit);
        this._maxInput.removeEventListener('keydown', this._onNumberKeydown);
      }

      if (this._form && this._onFormSubmit) {
        this._form.removeEventListener('submit', this._onFormSubmit);
      }
    }

    parseNumber(value, fallbackValue) {
      var parsedValue = Number(value);
      return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
    }

    clamp(value) {
      var clampedValue = value;

      if (clampedValue < this._lowerBound) {
        clampedValue = this._lowerBound;
      }

      if (clampedValue > this._upperBound) {
        clampedValue = this._upperBound;
      }

      return clampedValue;
    }

    syncFromCurrentValues() {
      if (!this._minInput || !this._maxInput) return;

      var minValue = this.parseNumber(this._minInput.value, this._lowerBound);
      var maxValue = this.parseNumber(this._maxInput.value, this._upperBound);

      this.setValues(minValue, maxValue, 'max');
    }

    onRangeInput(event) {
      var target = event.currentTarget;
      var minValue = this.parseNumber(this._minRange.value, this._lowerBound);
      var maxValue = this.parseNumber(this._maxRange.value, this._upperBound);
      var source = target === this._minRange ? 'min' : 'max';

      this.setValues(minValue, maxValue, source);
    }

    onNumberInput(event) {
      var target = event.currentTarget;

      if (!(target instanceof HTMLInputElement)) {
        return;
      }

      if (target.value === '') {
        return;
      }

      var minValue = this.parseNumber(this._minInput.value, this._lowerBound);
      var maxValue = this.parseNumber(this._maxInput.value, this._upperBound);
      var source = target === this._minInput ? 'min' : 'max';

      this.setValues(minValue, maxValue, source);
    }

    onNumberKeydown(event) {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      this.onCommit(event);
    }

    onCommit(event) {
      var minValue = this.parseNumber(
        this._minInput.value === '' ? this._lowerBound : this._minInput.value,
        this._lowerBound
      );
      var maxValue = this.parseNumber(
        this._maxInput.value === '' ? this._upperBound : this._maxInput.value,
        this._upperBound
      );
      var source = 'max';

      if (event && event.currentTarget) {
        if (
          event.currentTarget === this._minInput ||
          event.currentTarget === this._minRange
        ) {
          source = 'min';
        }
      }

      this.setValues(minValue, maxValue, source);
      this.submitForm();
    }

    onFormSubmit() {
      this.prepareForSubmission();
    }

    setValues(minValue, maxValue, source) {
      var nextMin = this.clamp(minValue);
      var nextMax = this.clamp(maxValue);

      if (source == 'min' && nextMin > nextMax) {
        nextMax = nextMin;
      }

      if (source == 'max' && nextMax < nextMin) {
        nextMin = nextMax;
      }

      this._minRange.value = String(nextMin);
      this._maxRange.value = String(nextMax);
      this._minInput.value = String(nextMin);
      this._maxInput.value = String(nextMax);

      this.updateTrack(nextMin, nextMax);
      this.syncHiddenInputs(nextMin, nextMax);
    }

    updateTrack(minValue, maxValue) {
      var totalRange = this._upperBound - this._lowerBound;
      var startPercentage = 0;
      var endPercentage = 100;

      if (totalRange > 0) {
        startPercentage = ((minValue - this._lowerBound) / totalRange) * 100;
        endPercentage = ((maxValue - this._lowerBound) / totalRange) * 100;
      }

      this.style.setProperty('--price-range-start', startPercentage + '%');
      this.style.setProperty('--price-range-end', endPercentage + '%');
    }

    syncHiddenInputs(minValue, maxValue) {
      this._minHiddenInput.value =
        minValue <= this._lowerBound ? '' : String(minValue);
      this._maxHiddenInput.value =
        maxValue >= this._upperBound ? '' : String(maxValue);
    }

    prepareForSubmission() {
      var minValue = this.parseNumber(this._minInput.value, this._lowerBound);
      var maxValue = this.parseNumber(this._maxInput.value, this._upperBound);

      this.syncHiddenInputs(minValue, maxValue);
    }

    submitForm() {
      if (!this._form) {
        return;
      }

      this.prepareForSubmission();

      if (typeof this._form.requestSubmit === 'function') {
        this._form.requestSubmit();
        return;
      }

      this._form.submit();
    }
  }

  customElements.define('price-range', PriceRangeElement);
})();
